import { useState } from 'react';
import { Link } from 'react-router-dom';
import { downloadPdf, docHref, type DocLink } from '../api';

/**
 * Inter-maillage des écrans. Toute référence affichée quelque part (produit, lot,
 * document d'origine, bon de transfert) mène à l'écran qui la détaille — le client
 * ne doit jamais avoir à retaper une référence dans une autre page.
 *
 * Conventions d'URL (paramètres en français, comme `?tab=`) :
 *   /stocks?produit=<id>   fiche produit ouverte
 *   /lots?lot=<id>         traçabilité d'un lot   (?q=<texte> = recherche)
 *   /achats?facture=<id>   facture fournisseur ouverte
 *   /ventes?vente=<id>     vente ouverte (onglet Suivi)
 */

// Odoo préfixe display_name par « [code] » : le code seul suffit comme libellé de lien.
export function productCode(name: string | null | undefined): string | null {
  const m = /^\[([^\]]+)\]/.exec(String(name ?? '').trim());
  return m ? (m[1] ?? null) : null;
}
export function stripCode(name: string | null | undefined): string {
  return String(name ?? '').replace(/^\[[^\]]+\]\s*/, '');
}

/** Référence produit → fiche produit de l'écran Stocks. */
export function ProductLink({
  id, children, title,
}: { id: number | null | undefined; children: React.ReactNode; title?: string }) {
  if (!id) return <>{children}</>;
  return (
    <Link className="link-btn" to={`/stocks?produit=${id}`} title={title ?? 'Voir la fiche produit et ses lots'}>
      {children}
    </Link>
  );
}

/** N° de lot → écran Traçabilité. `id` connu ⇒ lien direct, sinon recherche par nom. */
export function LotLink({ id, name }: { id?: number | null; name: string }) {
  const to = id ? `/lots?lot=${id}` : `/lots?q=${encodeURIComponent(name)}`;
  return <Link className="link-btn" to={to} title="Traçabilité du lot">{name}</Link>;
}

/** Origine d'un mouvement (« S00149 », « Facture … ») → la vente ou la facture. */
export function OriginLink({ link, label }: { link: DocLink | null | undefined; label: string | null }) {
  if (!label) return <>—</>;
  if (!link) return <>{label}</>;
  return (
    <Link className="link-btn" to={docHref(link)}
      title={link.kind === 'vente' ? 'Ouvrir la vente' : 'Ouvrir la facture fournisseur'}>
      {label}
    </Link>
  );
}

/**
 * Nom d'un bon de transfert → son PDF. Le rendu QWeb passe par une session Odoo
 * authentifiée par mot de passe : quand il échoue, le lien le dit sur place plutôt
 * que d'emmener l'utilisateur sur une page d'erreur.
 */
export function PickingPdfLink({ id, name }: { id: number | null | undefined; name: string | null }) {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!name) return <>—</>;
  if (!id) return <>{name}</>;
  if (err) return <span className="link-failed" title={err}>{name} · PDF indisponible</span>;
  return (
    <button
      className="link-btn"
      disabled={busy}
      title="Télécharger le PDF du transfert"
      onClick={(e) => {
        e.stopPropagation();
        setBusy(true);
        downloadPdf(`/api/stock/pickings/${id}/pdf`)
          .catch((x) => setErr(x.message ?? 'Erreur'))
          .finally(() => setBusy(false));
      }}
    >{busy ? `${name} …` : `${name} ⬇`}</button>
  );
}
